import { useState } from 'react';
import { Download, Receipt } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { downloadUrl } from '@/api/client';
import { useSalesList } from '@/api/hooks/useSales';
import type { Sale } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ReturnDialog } from '@/features/sales/ReturnDialog';
import { SaleCard } from '@/features/sales/SaleCard';
import { money } from '@/lib/utils';

export function SalesHistoryPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? '1');

  const { data, isLoading } = useSalesList(page, 30);
  const [returnTarget, setReturnTarget] = useState<Sale | null>(null);

  function goToPage(next: number) {
    setParams((prev) => {
      const copy = new URLSearchParams(prev);
      copy.set('page', String(next));
      return copy;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">Sales history</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.total} transaction(s)` : 'Loading…'}
          </p>
        </div>
        <Can permission={PERMISSIONS.salesExport}>
          <Button variant="outline" asChild>
            <a href={downloadUrl('/api/export/sales/csv')}>
              <Download /> Export CSV
            </a>
          </Button>
        </Can>
      </div>

      {data && (
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-t-4 border-t-primary p-4">
            <p className="tabular text-2xl font-bold text-primary">{money(data.todayStats.total)}</p>
            <p className="text-xs text-muted-foreground">Today's revenue</p>
          </Card>
          <Card className="border-t-4 border-t-status-ok p-4">
            <p className="tabular text-2xl font-bold text-status-ok">{data.todayStats.count}</p>
            <p className="text-xs text-muted-foreground">Today's sales</p>
          </Card>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : !data || data.items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <Receipt className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              No sales yet — generate your first bill to see records here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.items.map((sale) => (
            <SaleCard key={sale.id} sale={sale} onReturn={() => setReturnTarget(sale)} />
          ))}
        </div>
      )}

      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
            Previous
          </Button>
          <span className="tabular text-sm text-muted-foreground">
            Page {page} of {Math.ceil(data.total / data.pageSize)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= Math.ceil(data.total / data.pageSize)}
            onClick={() => goToPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <ReturnDialog sale={returnTarget} open={returnTarget !== null} onOpenChange={(open) => !open && setReturnTarget(null)} />
    </div>
  );
}